import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Shield, DollarSign, AlertTriangle, UserX, Eye } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const ALIGNMENT_STYLE = {
  compromised: { color: '#C9A84C', label: 'COMPROMISED' },
  corrupt: { color: '#DC2626', label: 'CORRUPT' },
};

const CriminalCounterPlay = ({ gameId, onBack }) => {
  const [data, setData] = useState(null);
  const [acting, setActing] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/police/counterplay/${gameId}`);
      if (res.ok) setData(await res.json());
    } catch (e) {}
    setLoading(false);
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  const act = async (officerId, action) => {
    setActing(`${officerId}_${action}`); setResult(null);
    try {
      const res = await fetch(`${API}/api/police/counterplay/${gameId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ officer_id: officerId, action }),
      });
      const d = await res.json();
      setResult({ officerId, ...d, error: !res.ok ? d.detail : null });
      load();
    } catch (e) { setResult({ officerId, error: 'Network error' }); }
    setActing(null);
  };

  if (loading) return <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs text-center py-8">Loading...</p>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="counterplay-view">
      <button onClick={onBack} className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#C9A84C]" data-testid="cp-back">BACK</button>

      <div className="text-center">
        <h3 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#C9A84C]">THE INSIDE MAN</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">BRIBE. FLIP. BLACKMAIL. EVERY COP HAS A PRICE.</p>
      </div>

      {data.flipped_count > 0 && (
        <div className="bg-[#4ADE80]/5 border border-[#4ADE80]/20 rounded-lg p-3 text-center">
          <p className="font-['Outfit'] text-[#4ADE80] text-xs">{data.flipped_count} officer(s) flipped — Raid effectiveness reduced by {data.raid_reduction}%</p>
        </div>
      )}

      <div className="space-y-2">
        {data.officers.map((officer, i) => {
          const align = ALIGNMENT_STYLE[officer.alignment] || ALIGNMENT_STYLE.compromised;
          const officerResult = result?.officerId === officer.id ? result : null;

          return (
            <motion.div key={officer.id}
              initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className={`bg-[#0a0e12] border rounded-xl p-3 ${
                officer.status === 'FLIPPED' ? 'border-[#4ADE80]/20' : officer.status === 'BRIBED' ? 'border-[#C9A84C]/20' : 'border-[#1A2535]'
              }`}
              data-testid={`officer-${officer.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h5 className="font-['Outfit'] text-xs font-bold text-[#E8E0D0]">{officer.name}</h5>
                  <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/30">{officer.position} — {officer.state.toUpperCase()}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="px-1.5 py-0.5 rounded text-[6px] tracking-widest font-['Outfit']"
                    style={{ color: align.color, background: align.color + '15', border: `1px solid ${align.color}30` }}>
                    {align.label}
                  </span>
                  {officer.status !== 'AVAILABLE' && (
                    <span className={`px-1.5 py-0.5 rounded text-[6px] tracking-widest font-['Outfit'] ${
                      officer.status === 'FLIPPED' ? 'text-[#4ADE80] bg-[#4ADE80]/10' : 'text-[#C9A84C] bg-[#C9A84C]/10'
                    }`}>{officer.status}</span>
                  )}
                </div>
              </div>

              <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/20 mb-2">
                Intel: {officer.intel_type.replace('_', ' ')} | Raid reduction: -{officer.effectiveness_reduction}%
              </p>

              {officerResult && (
                <div className={`px-3 py-2 rounded-lg mb-2 ${officerResult.error ? 'bg-[#DC2626]/5 border border-[#DC2626]/15' : 'bg-[#4ADE80]/5 border border-[#4ADE80]/15'}`}>
                  <p className={`font-['Outfit'] text-[9px] ${officerResult.error ? 'text-[#DC2626]' : 'text-[#4ADE80]'}`}>
                    {officerResult.narrator || officerResult.error}
                  </p>
                </div>
              )}

              {officer.status === 'AVAILABLE' && (
                <div className="flex gap-1.5">
                  <button onClick={() => act(officer.id, 'bribe')} disabled={acting?.startsWith(officer.id)}
                    className="flex-1 py-1.5 bg-[#C9A84C]/10 border border-[#C9A84C]/20 rounded-lg text-[#C9A84C] font-['Outfit'] text-[8px] tracking-wider hover:bg-[#C9A84C]/20 transition-colors"
                    data-testid={`bribe-${officer.id}`}>
                    <DollarSign size={10} className="inline -mt-0.5 mr-0.5" />BRIBE (${officer.bribe_cost.toLocaleString()})
                  </button>
                  <button onClick={() => act(officer.id, 'flip')} disabled={acting?.startsWith(officer.id)}
                    className="flex-1 py-1.5 bg-[#4ADE80]/10 border border-[#4ADE80]/20 rounded-lg text-[#4ADE80] font-['Outfit'] text-[8px] tracking-wider hover:bg-[#4ADE80]/20 transition-colors"
                    data-testid={`flip-${officer.id}`}>
                    <UserX size={10} className="inline -mt-0.5 mr-0.5" />FLIP (${officer.flip_cost.toLocaleString()})
                  </button>
                  <button onClick={() => act(officer.id, 'blackmail')} disabled={acting?.startsWith(officer.id)}
                    className="py-1.5 px-2 bg-[#DC2626]/10 border border-[#DC2626]/20 rounded-lg text-[#DC2626] font-['Outfit'] text-[8px] hover:bg-[#DC2626]/20 transition-colors"
                    data-testid={`blackmail-${officer.id}`}
                    title="Cheaper but 30% chance they report you (+15 heat)">
                    <AlertTriangle size={10} />
                  </button>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default CriminalCounterPlay;
